import { atom } from 'jotai';
import { RESET } from 'jotai/utils';

import {
  antmanConfigAtom,
  antmanEnableAtom,
  antmanFeaturesCapturedAtom,
  antmanInferenceCountAtom,
  antmanInferenceQueueAtom,
  antmanInferenceReportIndexAtom,
  antmanInferenceReportTimerAtom,
  antmanLatencySumAtom,
  antmanSuspendedAtom,
  findMLFeatureCondition,
  humanVisionPipeAtom,
  hvmFeatureConfigsAtom,
  hvmFeaturesCapturedAtom,
  hvmInferenceCountAtom,
  hvmInferenceQueueAtom,
  hvmInferenceReportIndexAtom,
  hvmInferenceReportTimerAtom,
  hvmLatencySumAtom,
  hvmModelConfigAtom,
  hvmSuspendedAtom,
  lidInferenceCountAtom,
  lidInferenceQueueAtom,
  lidInferenceReportIndexAtom,
  lidInferenceReportTimerAtom,
  lidLatencySumAtom,
  lidModelConfigAtom,
  lidPipeAtom,
} from 'src/stores/ml/atoms';
import { MLFeatureType, MLModelType } from 'src/stores/ml/types';
import { MLMatchMetrics, MLMatchSummary, ReportEnabledModelInfo } from 'src/types/Match';

import {
  reportMLMatchInferencesAPI,
  reportMLMatchMetricsAPI,
  reportMLMatchSummaryAPI,
} from './apis';
import { matchInfoAtom } from './atoms';

/**
 * 서버가 reportEnabledModelInfos에 해당 모델을 포함하지 않으면 undefined를 반환한다.
 * 호출부에서 undefined일 때 report timer를 생성하지 않아 periodic report를 비활성화한다.
 */
export function getReportIntervalMs(
  modelType: MLModelType,
  reportEnabledModelInfos?: ReportEnabledModelInfo[]
): number | undefined {
  const reportIntervalMs = reportEnabledModelInfos?.find(
    (info) => info.model === modelType
  )?.reportIntervalMs;
  if (reportIntervalMs == null || !Number.isFinite(reportIntervalMs) || reportIntervalMs <= 0) {
    return undefined;
  }
  return reportIntervalMs;
}

export const MLMetricsAtom = atom<MLMatchMetrics['actions']>([]);

// --- Summary payload helpers (Antman / HVM / LID) ---

function createSummaryFeatureReport({
  type,
  model,
  taskIndex,
  criteriaWindow,
  inferenceIndex,
}: {
  type: MLFeatureType;
  model: MLModelType;
  taskIndex?: number;
  criteriaWindow?: number;
  inferenceIndex?: number;
}): MLMatchSummary['matchSummaryFeatureReports'][number] {
  return {
    type,
    machineLearningThresholdInference:
      inferenceIndex != null && taskIndex != null
        ? [{ model, taskIndex, index: inferenceIndex }]
        : [],
    machineLearningFeatureTriggerInferences:
      inferenceIndex != null && taskIndex != null && criteriaWindow != null
        ? [{ model, taskIndex, index: inferenceIndex, criteriaWindow, triggeredCount: 1 }]
        : [],
  };
}

const resetMLReportStateAtom = atom(null, (_get, set) => {
  set(antmanSuspendedAtom, RESET);
  set(hvmSuspendedAtom, RESET);
  set(antmanInferenceCountAtom, RESET);
  set(antmanInferenceQueueAtom, RESET);
  set(antmanInferenceReportIndexAtom, RESET);
  set(antmanLatencySumAtom, RESET);
  set(hvmInferenceCountAtom, RESET);
  set(hvmInferenceQueueAtom, RESET);
  set(hvmInferenceReportIndexAtom, RESET);
  set(hvmLatencySumAtom, RESET);
  set(lidInferenceCountAtom, RESET);
  set(lidInferenceQueueAtom, RESET);
  set(lidInferenceReportIndexAtom, RESET);
  set(lidLatencySumAtom, RESET);
});

const bufferAntmanMatchEndMetricsAtom = atom(null, (get, set, reconnect: boolean) => {
  const antmanFeaturesCaptured = get(antmanFeaturesCapturedAtom);
  const matchBlurCaptured = antmanFeaturesCaptured[MLFeatureType.MatchBlur];
  const matchAutoReportImageCaptured = antmanFeaturesCaptured[MLFeatureType.MatchAutoReportImage];
  const metricsToAdd = [
    ...(matchAutoReportImageCaptured
      ? [
          {
            triggeredAt: matchAutoReportImageCaptured.triggeredAt,
            action: 'reported',
            feature: MLFeatureType.MatchAutoReportImage,
            modelType: MLModelType.Antman,
            actedAt: new Date().getTime(),
          },
        ]
      : []),
    ...(matchBlurCaptured && !matchBlurCaptured.handled
      ? [
          {
            triggeredAt: matchBlurCaptured.triggeredAt,
            action: !reconnect ? 'leaved' : 'blurred',
            feature: MLFeatureType.MatchBlur,
            modelType: MLModelType.Antman,
            actedAt: new Date().getTime(),
          },
        ]
      : []),
  ];

  if (metricsToAdd.length > 0) {
    set(MLMetricsAtom, get(MLMetricsAtom).concat(metricsToAdd));
  }
});

// --- Periodic inference flush (Beacon period API) ---

export const reportAntmanInferenceAtom = atom(null, (get, set) => {
  const matchInfo = get(matchInfoAtom);
  if (!matchInfo || !matchInfo.matchId) return;

  const inferences = get(antmanInferenceQueueAtom);
  if (inferences.length === 0) return;

  const reportIndex = get(antmanInferenceReportIndexAtom);

  set(antmanInferenceQueueAtom, RESET);
  set(antmanInferenceReportIndexAtom, (prev) => prev + 1);

  // Inference logging은 작업 실패에 대한 처리 없음
  reportMLMatchInferencesAPI({
    matchId: matchInfo.matchId,
    models: [
      {
        modelId: get(antmanConfigAtom).modelId,
        index: reportIndex,
        inferences,
      },
    ],
  });
});

export const reportHvmInferencesAtom = atom(null, (get, set) => {
  const matchInfo = get(matchInfoAtom);
  if (!matchInfo?.matchId) return;

  const inferences = get(hvmInferenceQueueAtom);
  if (inferences.length === 0) return;

  const reportIndex = get(hvmInferenceReportIndexAtom);

  set(hvmInferenceQueueAtom, RESET);
  set(hvmInferenceReportIndexAtom, (prev) => prev + 1);

  reportMLMatchInferencesAPI({
    matchId: matchInfo.matchId,
    models: [
      {
        modelId: get(hvmModelConfigAtom).modelId,
        index: reportIndex,
        inferences,
      },
    ],
  });
});

export const clearMLInferenceIntervalAtom = atom(null, (get, set) => {
  const timerId = get(antmanInferenceReportTimerAtom);

  if (timerId !== null) {
    clearInterval(timerId);
    set(antmanInferenceReportTimerAtom, null);
  }
});

export const clearHvmInferenceIntervalAtom = atom(null, (get, set) => {
  const timerId = get(hvmInferenceReportTimerAtom);

  if (timerId !== null) {
    clearInterval(timerId);
    set(hvmInferenceReportTimerAtom, null);
  }
});

export const reportLidInferencesAtom = atom(null, (get, set) => {
  const matchInfo = get(matchInfoAtom);
  if (!matchInfo?.matchId) return;

  const inferences = get(lidInferenceQueueAtom);
  if (inferences.length === 0) return;

  const modelConfig = get(lidModelConfigAtom);
  if (!modelConfig) return;

  const reportIndex = get(lidInferenceReportIndexAtom);

  set(lidInferenceQueueAtom, RESET);
  set(lidInferenceReportIndexAtom, (prev) => prev + 1);

  reportMLMatchInferencesAPI({
    matchId: matchInfo.matchId,
    models: [
      {
        modelId: modelConfig.modelId,
        index: reportIndex,
        inferences,
      },
    ],
  });
});

export const clearLidInferenceIntervalAtom = atom(null, (get, set) => {
  const timerId = get(lidInferenceReportTimerAtom);

  if (timerId !== null) {
    clearInterval(timerId);
    set(lidInferenceReportTimerAtom, null);
  }
});

// --- Match-end summary assembly ---

type MLMatchSummaryPart = Pick<
  MLMatchSummary,
  'matchSummaryModelReports' | 'matchSummaryFeatureReports'
>;

const EMPTY_ML_MATCH_SUMMARY_PART = (): MLMatchSummaryPart => ({
  matchSummaryModelReports: [],
  matchSummaryFeatureReports: [],
});

const antmanMatchSummaryPartAtom = atom((get): MLMatchSummaryPart => {
  if (!(get(antmanEnableAtom) || get(antmanSuspendedAtom))) {
    return EMPTY_ML_MATCH_SUMMARY_PART();
  }

  const antmanFeaturesCaptured = get(antmanFeaturesCapturedAtom);
  const matchBlurCaptured = antmanFeaturesCaptured[MLFeatureType.MatchBlur];
  const matchAutoReportImageCaptured = antmanFeaturesCaptured[MLFeatureType.MatchAutoReportImage];
  const antmanInferenceCount = get(antmanInferenceCountAtom);
  const antmanConfig = get(antmanConfigAtom);
  const blurConfig = findMLFeatureCondition(antmanConfig, MLFeatureType.MatchBlur);
  const autoReportImageConfig = findMLFeatureCondition(
    antmanConfig,
    MLFeatureType.MatchAutoReportImage
  );
  const antmanFeatureReports = [
    ...(blurConfig
      ? [
          createSummaryFeatureReport({
            type: MLFeatureType.MatchBlur,
            model: MLModelType.Antman,
            taskIndex: blurConfig.modelIndex,
            criteriaWindow: blurConfig.criteriaWindow,
            inferenceIndex: matchBlurCaptured?.inferenceIndex,
          }),
        ]
      : []),
    ...(autoReportImageConfig
      ? [
          createSummaryFeatureReport({
            type: MLFeatureType.MatchAutoReportImage,
            model: MLModelType.Antman,
            taskIndex: autoReportImageConfig.modelIndex,
            criteriaWindow: autoReportImageConfig.criteriaWindow,
            inferenceIndex: matchAutoReportImageCaptured?.inferenceIndex,
          }),
        ]
      : []),
  ];

  return {
    matchSummaryModelReports: [
      {
        modelId: antmanConfig.modelId,
        inferencesCount: antmanInferenceCount,
        averageLatencyMs:
          antmanInferenceCount > 0
            ? Math.round(get(antmanLatencySumAtom) / antmanInferenceCount)
            : 0,
        usedGPU: false,
        usedXNNPACK: true,
        suspendPeriodicReport: get(antmanSuspendedAtom),
      },
    ],
    matchSummaryFeatureReports: antmanFeatureReports,
  };
});

const hvmMatchSummaryPartAtom = atom((get): MLMatchSummaryPart => {
  const hvmActive = get(humanVisionPipeAtom) !== null || get(hvmSuspendedAtom);
  if (!hvmActive) {
    return EMPTY_ML_MATCH_SUMMARY_PART();
  }

  const hvmFeaturesCaptured = get(hvmFeaturesCapturedAtom);
  const hvmInferenceCount = get(hvmInferenceCountAtom);
  const hvmFeatureConfigs = get(hvmFeatureConfigsAtom);
  const hvmFeatureConditions = hvmFeatureConfigs.flatMap((featureConfig) => {
    const condition = featureConfig.featureCondition.find(
      (featureCondition) => featureCondition.modelType === MLModelType.HumanVision
    );
    return condition ? [{ featureType: featureConfig.featureType, condition }] : [];
  });

  return {
    matchSummaryModelReports: [
      {
        modelId: get(hvmModelConfigAtom).modelId,
        inferencesCount: hvmInferenceCount,
        averageLatencyMs:
          hvmInferenceCount > 0 ? Math.round(get(hvmLatencySumAtom) / hvmInferenceCount) : 0,
        usedGPU: false,
        usedXNNPACK: true,
        suspendPeriodicReport: get(hvmSuspendedAtom),
      },
    ],
    matchSummaryFeatureReports: hvmFeatureConditions.map(({ featureType, condition }) =>
      createSummaryFeatureReport({
        type: featureType,
        model: MLModelType.HumanVision,
        taskIndex: condition.modelIndex,
        criteriaWindow: condition.criteriaWindow,
        inferenceIndex: hvmFeaturesCaptured[featureType]?.inferenceIndex,
      })
    ),
  };
});

/** LID summary — 리포트 전용이므로 featureReports는 빈 배열 */
const lidMatchSummaryPartAtom = atom((get): MLMatchSummaryPart => {
  const lidConfig = get(lidModelConfigAtom);
  const lidInferenceCount = get(lidInferenceCountAtom);
  const lidActive = get(lidPipeAtom) !== null || lidInferenceCount > 0;
  if (!lidConfig || !lidActive) {
    return EMPTY_ML_MATCH_SUMMARY_PART();
  }

  return {
    matchSummaryModelReports: [
      {
        modelId: lidConfig.modelId,
        inferencesCount: lidInferenceCount,
        averageLatencyMs:
          lidInferenceCount > 0 ? Math.round(get(lidLatencySumAtom) / lidInferenceCount) : 0,
        usedGPU: false,
        usedXNNPACK: true,
        suspendPeriodicReport: false,
      },
    ],
    matchSummaryFeatureReports: [],
  };
});

// --- Match disconnect: flush queues → summary API → reset counters → Antman-only metrics buffer ---

export const reportMLAtom = atom(null, (get, set, reconnect: boolean) => {
  const matchId = get(matchInfoAtom)?.matchId;

  // Summary 계산 전에 남아있는 periodic inference 큐를 flush한다.
  if (matchId && (get(antmanEnableAtom) || get(antmanSuspendedAtom))) {
    set(reportAntmanInferenceAtom);
  }
  if (matchId && (get(humanVisionPipeAtom) !== null || get(hvmSuspendedAtom))) {
    set(reportHvmInferencesAtom);
  }
  if (matchId && get(lidModelConfigAtom)) {
    set(reportLidInferencesAtom);
  }

  const antmanSummaryReports = matchId
    ? get(antmanMatchSummaryPartAtom)
    : EMPTY_ML_MATCH_SUMMARY_PART();
  const hvmSummaryReports = matchId ? get(hvmMatchSummaryPartAtom) : EMPTY_ML_MATCH_SUMMARY_PART();
  const lidSummaryReports = matchId ? get(lidMatchSummaryPartAtom) : EMPTY_ML_MATCH_SUMMARY_PART();
  const matchSummaryModelReports = [
    ...antmanSummaryReports.matchSummaryModelReports,
    ...hvmSummaryReports.matchSummaryModelReports,
    ...lidSummaryReports.matchSummaryModelReports,
  ];
  const matchSummaryFeatureReports = [
    ...antmanSummaryReports.matchSummaryFeatureReports,
    ...hvmSummaryReports.matchSummaryFeatureReports,
    ...lidSummaryReports.matchSummaryFeatureReports,
  ];

  // Summary는 매치 종료 시점의 모델/feature 상태 스냅샷이므로 disconnect 직후 바로 전송해도 된다.
  // 반면 metrics(actions)는 after-match popup의 report/dismissed처럼 cleanup 이후에 확정되는 액션이 있어
  // handleDisconnectAtom에서 popup 완료 후 별도 전송한다.

  if (matchId && matchSummaryModelReports.length > 0) {
    reportMLMatchSummaryAPI({
      matchId,
      matchSummaryModelReports,
      matchSummaryFeatureReports,
    });
  }

  set(resetMLReportStateAtom);
  set(bufferAntmanMatchEndMetricsAtom, reconnect);
});

// --- ML metrics (Beacon; matchId 인자 — disconnect 후 matchInfo reset 대응) ---

export const reportMLMetricsAtom = atom(null, (get, _set, matchId?: string) => {
  if (!matchId) return;

  const actions = get(MLMetricsAtom);
  if (actions.length === 0) return;

  // Metrics는 popup 이후에 추가되는 action까지 포함해야 하므로
  // matchInfoAtom이 reset된 뒤에도 호출할 수 있게 matchId를 인자로 받는다.
  reportMLMatchMetricsAPI({
    matchId,
    actions,
  });
});
